import React from 'react';
import { Truck, Leaf, Building2, TrafficCone, Wind, Route } from 'lucide-react';

const CITY_IMG = 'https://static.prod-images.emergentagent.com/jobs/0ca1b2c0-495a-4032-aa3f-8b74f40e0c75/images/5c50bcbd42b7f3e6eb7af5d6ecf32672c929151448e5821a3ccb44e1ccccba35.jpeg';

const BENEFITS = [
  { icon: TrafficCone, title: 'Reduzimos o trânsito', text: 'Menos congestionamentos e ruas mais livres para todos.' },
  { icon: Wind, title: 'Diminuímos as emissões', text: 'Menos poluição atmosférica no coração da cidade e nos bairros onde vivemos.' },
  { icon: Route, title: 'Otimizamos viagens', text: 'Conectamos cargas a veículos que já estão em circulação ou disponíveis na zona, evitando rotas desnecessárias.' },
];

const PILLARS = [
  { icon: Truck, title: 'Menos Pesados, Mais Agilidade', text: 'Substituímos grandes camiões por carrinhas eficientes e proporcionais às necessidades reais de cada transporte local.' },
  { icon: Leaf, title: 'Redução de Emissões', text: 'Menos combustível desperdiçado e menor pegada de carbono no ar das nossas cidades e zonas urbanas.' },
  { icon: Building2, title: 'Ruas Mais Livres', text: 'Reduzimos os congestionamentos e o bloqueio de vias, tornando o trânsito mais fluído para todos.' },
];

export const SustainabilitySection = () => (
  <section id="sustentabilidade" className="py-24 px-6 md:px-12 lg:px-24 bg-[#f0fdf8]" data-testid="sustainability-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-14 items-center mb-20">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#10b981] mb-4">Sustentabilidade</div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-3" data-testid="sustainability-title">
            Por cidades mais limpas, verdes e sem trânsito.
          </h2>
          <p className="text-lg text-[#059669] font-medium mb-6">Cidades mais fluídas, ar mais limpo.</p>
          <p className="text-slate-600 leading-relaxed mb-4">
            Grandes camiões e veículos pesados de mercadorias não pertencem ao centro das nossas cidades. Eles bloqueiam o trânsito, aumentam as emissões de carbono e sobrecarregam as vias urbanas.
          </p>
          <p className="text-slate-600 leading-relaxed mb-8">
            Na TugGO, promovemos um transporte mais inteligente e sustentável. Ao substituir grandes veículos pesados por carrinhas otimizadas para entregas locais:
          </p>
          <ul className="space-y-5 mb-8">
            {BENEFITS.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex gap-4">
                <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-white shadow-sm flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#10b981]" />
                </div>
                <p className="text-slate-700 leading-relaxed">
                  <strong className="text-slate-900">{title}:</strong> {text}
                </p>
              </li>
            ))}
          </ul>
          <p className="text-slate-900 font-semibold text-lg border-l-4 border-[#10b981] pl-4">
            Mover o que precisa não tem de custar o planeta. Com a TugGO, cada transporte é uma escolha mais consciente.
          </p>
        </div>
        <div className="relative">
          <img src={CITY_IMG} alt="Carrinha de entregas numa rua verde e sem trânsito em Lisboa" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" />
          <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-3 bg-white rounded-2xl shadow-lg px-5 py-4">
            <Leaf className="w-6 h-6 text-[#10b981]" />
            <span className="text-sm font-semibold text-slate-900">Menos camiões no centro da cidade</span>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {PILLARS.map(({ icon: Icon, title, text }, i) => (
          <div
            key={title}
            className="bg-white rounded-2xl p-8 border border-[#d1fae5] hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300"
            data-testid={`sustainability-pillar-${i}`}
          >
            <div className="w-14 h-14 bg-[#d1fae5] rounded-2xl flex items-center justify-center mb-6">
              <Icon className="w-7 h-7 text-[#059669]" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">{title}</h3>
            <p className="text-slate-600 leading-relaxed">{text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
